import {
  Box,
  Text,
  Heading,
  Button,
  Stack,
  Breadcrumb,
  ResponsiveRender
} from '@machobear/design-system'

import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import useSearchParams from '@/hooks/use-search-params'
import useProfile from '@/hooks/use-profile'
import useGetAutoReplyMessages from '@/hooks/use-get-auto-reply-message'

import MessageCard from './sub-components/MessageCard'
import PopoverUpgrade from './sub-components/PopoverUpgrade'
import AutoReplyEmptyComponent from './sub-components/EmptyMessage'
import AutoReplyPageWrapper from './sub-components/AutoReplyPageWrapper'
import LoadingAutoReply from './sub-components/LoadingAutoReply'

type MessageData = {
  id: string
  auto_reply_trigger_txt_id: string
  company_id: string
  name: string
  content: string
  attach_new_listings: string
  is_active: string
  user_id: string
  created_at: string
  updated_at: string | null
  listing_count: string
}

const ManageAutoReply = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const { trigger = 'first_tenant_message_received' }: { trigger?: keyof typeof AutoReplyTitles } =
    useSearchParams()
  const profileData = useProfile()
  const isEssentialPlan = profileData?.company_info?.base_plan_name === 'essentials-plan-monthly'
  const { data, error, isLoading } = useGetAutoReplyMessages(trigger)

  const AutoReplyTitles = {
    first_tenant_message_received: t(
      'l.settings.view.auto_reply_first_tenant_message_received.title',
      'First Tenant Message Received'
    ),
    every_tenant_message_received_5_minutes: t(
      'l.settings.view.auto_reply_every_tenant_message_received_5_minutes.title',
      'Every Tenant Message Received 5 minutes'
    ),
    first_tenant_message_received_for_the_day: t(
      'l.settings.view.auto_reply_first_tenant_message_received_for_the_day.title',
      'First Tenant Message Received For The Day'
    ),
    application_received: t(
      'l.settings.view.auto_reply_application_received.title',
      'Application Received'
    ),
    invited_to_apply: t('l.settings.view.auto_reply_invited_to_apply.title', 'Invited To Apply'),
    invited_to_apply_no_response_3_days: t(
      'l.settings.view.auto_reply_invited_to_apply_no_response_3_days.title',
      'Invited To Apply No Response 3 Days'
    ),
    new_contract_fully_signed: t(
      'l.settings.view.auto_reply_new_contract_fully_signed.title',
      'New Contract Fully Signed'
    ),
    rent_payment_reminder_3_days_prior: t(
      'l.settings.view.auto_reply_rent_payment_reminder_3_days_prior.title',
      'Rent Payment Reminder 3 Days Prior'
    ),
    unit_has_been_rented: t(
      'l.settings.view.auto_reply_unit_has_been_rented.title',
      'Unit Has Been Rented'
    ),
    tenant_application_copied: t(
      'l.settings.view.auto_reply_tenant_application_copied.title',
      'Tenant Application Copied'
    )
  }

  const title = AutoReplyTitles[trigger]

  const addAutoReply = () => {
    history.push(`/settings/auto-reply/auto-reply-message/create?trigger=${trigger}`)
  }

  const editAutoReply = (id: string) => {
    history.push(`/settings/auto-reply/auto-reply-message/edit?trigger=${trigger}&id=${id}`)
  }

  return (
    <>
      <AutoReplyPageWrapper title={t('l.settings.view.auto_reply.label', 'Auto-Reply')}>
        <Box flexDirection="column" display="flex" gap="small" style={{ flex: 1 }}>
          <Box marginBottom={'xxsmall'}>
            <Breadcrumb
              variant="line"
              items={[
                {
                  label: t('b.settings.view.settings.title', 'Settings'),
                  href: `/settings`
                },
                {
                  label: t('l.settings.view.auto_reply.label', 'Auto-Reply'),
                  href: `/settings/auto-reply`
                },
                {
                  label: title
                }
              ]}
            />
          </Box>

          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'spaceBetween'}
            marginBottom={'medium'}
          >
            <Heading level="4">{title}</Heading>
            <ResponsiveRender above="mobile">
              <Box>
                {isEssentialPlan && data?.items.length !== 0 && data?.items.length < 2 ? (
                  <PopoverUpgrade hasButton={true} />
                ) : (
                  <Button size="small" variant={'solid'} onClick={addAutoReply}>
                    {t('l.settings.view.auto_reply.add_auto_reply', 'Add Auto Reply')}
                  </Button>
                )}
              </Box>
            </ResponsiveRender>
          </Box>
          <Stack space="medium">
            {isLoading ? (
              <LoadingAutoReply />
            ) : error ? (
              <Text>
                {t('l.settings.view.auto_reply.error_loading_message', 'Error loading messages')}
              </Text>
            ) : data?.items && data.items.length > 0 ? (
              data.items.map((data: MessageData, index: string) => (
                <MessageCard
                  key={`${data.id} ${index}`}
                  id={data.id}
                  title={data.name}
                  description={data.content}
                  lastUpdate={data.updated_at ? data.updated_at : data.created_at}
                  listingsCount={data.listing_count}
                  isActive={data.is_active}
                  onClick={() => editAutoReply(data.id)}
                  trigger={trigger}
                />
              ))
            ) : (
              <AutoReplyEmptyComponent />
            )}
          </Stack>
        </Box>
      </AutoReplyPageWrapper>
      <ResponsiveRender below="tablet">
        <Box paddingX="gutter" width="full" position="fixed" bottom={0} marginBottom={'xsmall'}>
          {isEssentialPlan && data?.items.length !== 0 && data?.items.length < 2 ? (
            <PopoverUpgrade fullWidth hasButton={true} />
          ) : (
            <Button size="small" variant={'solid'} onClick={addAutoReply}>
              {t('l.settings.view.auto_reply.add_auto_reply', 'Add Auto Reply')}
            </Button>
          )}
        </Box>
      </ResponsiveRender>
    </>
  )
}

export default ManageAutoReply
